import { useState, useEffect } from 'react'
import { Clock, Menu, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import Button from '../ui/Button'

const LandingHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    const element = document.querySelector(href)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
      setIsMenuOpen(false)
    }
  }

  const scrollToContact = () => {
    const element = document.querySelector('#contato')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
      setIsMenuOpen(false)
    }
  }

  const navLinks = [
    { href: '#solucao', label: 'Solução' },
    { href: '#beneficios', label: 'Benefícios' },
    { href: '#funcionalidades', label: 'Funcionalidades' },
    { href: '#faq', label: 'FAQ' },
    { href: '#contato', label: 'Contato' }
  ]

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-200 ${isScrolled ? 'shadow-md' : ''}`}>
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between py-4">
          <a 
            href="#hero" 
            className="flex items-center gap-3 text-xl font-extrabold text-gray-900 hover:text-primary-600 transition-colors duration-200"
            onClick={(e) => handleNavClick(e, '#hero')}
          >
            <Clock size={32} className="text-primary-600" />
            <span>Flow Clock</span>
          </a>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="relative text-base font-semibold text-gray-700 hover:text-primary-600 transition-colors duration-200 group"
                onClick={(e) => handleNavClick(e, link.href)}
              >
                {link.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary-600 transition-all duration-200 group-hover:w-full" />
              </a>
            ))}
            <a href="https://app.flowclock.com.br/login" target="_blank" rel="noopener noreferrer">
              <Button variant="ghost" size="md">
                Entrar
              </Button>
            </a>
            <Button variant="primary" size="md" onClick={scrollToContact}>
              Solicitar Demonstração
            </Button>
          </nav>
          
          {/* Mobile Menu Toggle */}
          <button 
            className="lg:hidden p-2 text-gray-900 hover:text-primary transition-colors duration-200"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setIsMenuOpen(false)}
            />
            
            {/* Menu */}
            <motion.nav
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="lg:hidden fixed top-0 right-0 w-80 h-screen bg-white shadow-2xl z-50 flex flex-col items-start p-12 gap-6"
            >
              {navLinks.map((link, index) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  className="text-lg font-medium text-gray-900 hover:text-primary transition-colors duration-200 w-full"
                  onClick={(e) => handleNavClick(e, link.href)}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {link.label}
                </motion.a>
              ))}
              <a href="https://app.flowclock.com.br/login" target="_blank" rel="noopener noreferrer" className="w-full">
                <Button variant="outline" size="md" className="w-full">
                  Entrar
                </Button>
              </a>
              <Button variant="primary" size="md" onClick={scrollToContact} className="w-full">
                Solicitar Demonstração
              </Button>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </header>
  )
}

export default LandingHeader
